import { useI18n } from "#hooks/use-i18n"
import type { ExpenseCategory } from "@home-finance-manager/shared/types/expense_categories"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Label } from "../ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"

interface CategoryMatchingStepProps {
  allCsvCategories: Set<string>
  categories: ExpenseCategory[]
  categoryMappings: Map<string, number | "new">
  onCategoryMappingChange: (categoryName: string, categoryId: number | "new") => void
}

export function CategoryMatchingStep({
  allCsvCategories,
  categories,
  categoryMappings,
  onCategoryMappingChange,
}: CategoryMatchingStepProps) {
  const { t } = useI18n()

  if (allCsvCategories.size === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">
          {t("components.expenseImport.categoryMatchingTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t("components.expenseImport.categoryMatchingDescription")}
        </p>
        {Array.from(allCsvCategories).map((categoryName) => {
          const selectedCategoryId = categoryMappings.get(categoryName)
          const selectedCategory =
            typeof selectedCategoryId === "number"
              ? categories.find((c) => c.id === selectedCategoryId)
              : null

          return (
            <div
              key={categoryName}
              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {selectedCategory && (
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: selectedCategory.color,
                      boxShadow: `0 0 4px ${selectedCategory.color}40`,
                    }}
                  />
                )}
                <Label className="break-words sm:break-normal">{categoryName}</Label>
              </div>
              <Select
                value={
                  categoryMappings.get(categoryName) === "new"
                    ? "new"
                    : String(categoryMappings.get(categoryName) || "")
                }
                onValueChange={(value) => {
                  if (value === "new") {
                    onCategoryMappingChange(categoryName, "new")
                  } else {
                    onCategoryMappingChange(categoryName, Number(value))
                  }
                }}
              >
                <SelectTrigger className="w-full sm:w-48 flex-shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border-2 border-dashed border-muted-foreground/50 flex-shrink-0" />
                      <span>{t("components.expenseImport.createNewCategoryOption")}</span>
                    </div>
                  </SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: cat.color,
                            boxShadow: `0 0 4px ${cat.color}40`,
                          }}
                        />
                        <span>{cat.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
